import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { authService as suuntoAuthService } from '../services/api';
import { authService as jwtAuthService } from '../services/authService';
import { diveService } from '../services/diveService';
import { getUserIdFromToken, logTokenInfo } from '../utils/jwtUtils';
import { preferencesManager } from '../utils/preferencesManager';
import { getSuuntoRedirectUri } from '../utils/native';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    const handleAuthCallback = async () => {
      const code = searchParams.get('code');
      const error = searchParams.get('error');

      if (error) {
        console.error('Authorization error received:', error);
        setError(`Authorization failed: ${error}`);
        // Clear URL parameters to prevent retry
        window.history.replaceState({}, document.title, '/auth/callback');
        return;
      }

      if (!code) {
        console.error('No authorization code received');
        setError('No authorization code received');
        return;
      }

      // Check if this code has already been processed (stored in sessionStorage)
      const processedCode = sessionStorage.getItem('last_processed_auth_code');
      if (processedCode === code) {
        console.warn('Authorization code already processed - preventing reuse');
        setError('This authorization has already been processed. Please try connecting again if needed.');
        return;
      }

      try {
        const redirectUri = getSuuntoRedirectUri();
        console.log('Starting token exchange with redirect URI:', redirectUri);

        // Mark this code as being processed
        sessionStorage.setItem('last_processed_auth_code', code);

        const response = await suuntoAuthService.exchangeToken(code, redirectUri);

        if (response.accessToken) {
          // Clear the authorization code from URL to prevent reuse
          window.history.replaceState({}, document.title, '/auth/callback');

          localStorage.setItem('suunto_access_token', response.accessToken);

          // Extract user ID from token and set up preferences manager
          logTokenInfo(response.accessToken);
          const userId = getUserIdFromToken(response.accessToken);
          preferencesManager.setUserId(userId);

          // Load user preferences from database
          try {
            await preferencesManager.loadPreferences();
          } catch (error) {
            console.warn('Failed to load preferences, will create new:', error);
          }

          // Connect Suunto account to user's database record
          try {
            const connectResponse = await diveService.connectSuuntoAccount(response.accessToken);

            // Update user object in authService with new hasSuuntoConnection flag
            if (connectResponse.user) {
              jwtAuthService.updateUser(connectResponse.user);
            }

            // Automatically sync dives after connecting
            await diveService.syncFromSuunto();
          } catch (syncError: any) {
            console.error('Failed to connect/sync Suunto account:', syncError);
            // Don't block navigation on sync errors - user can manually sync later
          }

          // Clean up processed code marker
          sessionStorage.removeItem('last_processed_auth_code');
          window.location.href = '/';
        } else {
          console.error('No access token in response');
          throw new Error('No access token received');
        }
      } catch (err: any) {
        console.error('Token exchange error:', err);

        // Clear the authorization code from URL
        window.history.replaceState({}, document.title, '/auth/callback');

        if (err.response?.status === 400) {
          const errorMsg = err.response?.data?.error || err.message;
          console.error('OAuth 400 error details:', errorMsg);

          // Show the actual backend error message which now includes Suunto API details
          setError(errorMsg || 'Invalid authorization code. Please try connecting again.');
        } else {
          const errorMsg = err.response?.data?.error || err.message || 'Failed to complete authorization';
          setError(errorMsg + '\n\nPlease try again.');
        }

        // Clean up on error
        sessionStorage.removeItem('last_processed_auth_code');
      }
    };

    // Prevent double execution in React.StrictMode (development)
    if (hasProcessedRef.current) {
      return;
    }
    hasProcessedRef.current = true;

    handleAuthCallback();
  }, [searchParams, navigate]);

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="60vh"
        textAlign="center"
        p={3}
      >
        <Alert severity="error" sx={{ mb: 2, maxWidth: 600, textAlign: 'left', whiteSpace: 'pre-line' }}>
          {error}
        </Alert>
        <Typography variant="body1" sx={{ mt: 2 }}>
          You can close this window and try connecting again.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="60vh"
      textAlign="center"
    >
      <CircularProgress size={48} sx={{ mb: 2 }} />
      <Typography variant="h6">Connecting to Suunto...</Typography>
      <Typography variant="body2" color="textSecondary">
        Please wait while we complete the authorization process.
      </Typography>
    </Box>
  );
};

export default AuthCallback;
